import { Box } from '@material-ui/core';
import React from 'react';
import { FB, FBFieldMarkDown, FBFieldTopBar, FBInputProps, FBProvider, FBSchemaProps, FBSchemaState } from '..';
import useHover from '../../hooks/useHover';
import useFieldWrapperStyles from '../FBFieldWrapper/FBFieldWrapper.styles';
import FBDeviationProcedureState from './FBDeviationProcedure.state';
import { FBDeviationStepValue } from './FBDeviationProcedure.types';

interface MpiComponentProps {
  mpiId: string
  stepId: string
  stepData: FBDeviationStepValue
  name: string
  disabled?: boolean
  mpiSchema: FBSchemaProps[]
  position: number
  canEdit?: boolean
  onRemove?: () => void
  onEdit?: (item: FBSchemaProps) => void
}

// Renders a cloned non-procedure MPI component with its real control. Controls look themselves up by index/name in
// workspaceState.schema, so give them a workspace whose schema is the MPI's.
const FBDeviationMpiComponent: React.FC<MpiComponentProps> = ({
  stepId, name, mpiSchema, position, mpiId, stepData, canEdit, onRemove, onEdit,
}) => {
  const { workspaceState } = FB.useStores();
  const fieldClasses = useFieldWrapperStyles();
  const [hoverRef, isHovered] = useHover<HTMLDivElement>();
  const [isMarkDownVisible, setIsMarkDownVisible] = React.useState(false);
  // Keep the Proxy stable: a new workspace on each mpiSchema change makes the MobX Provider throw
  // "set of provided stores has changed". Read the live values through a ref instead.
  const latest = React.useRef({ mpiSchema, position, onRemove, onEdit });
  latest.current = { mpiSchema, position, onRemove, onEdit };
  const mpiWorkspace = React.useMemo(() => workspaceState && new Proxy(workspaceState, {
    get: (target, prop) => {
      const { mpiSchema, position, onRemove, onEdit } = latest.current;
      switch (prop) {
        case 'schema': return mpiSchema;
        case 'getSchemaItemAt': return (i?: number) => (i === undefined ? undefined : mpiSchema[i]);
        case 'getSchemaItem': return (n?: string) => mpiSchema.find((it) => it.name === n);
        case 'saveDocRev': return () => undefined; // edits are persisted via the deviation's formInput
        // never write cloned items into the deviation's own schema; the field toolbar's remove lands here
        case 'setSchemaItem': return (item?: FBSchemaProps) => { if (item?.deleted) { onRemove?.(); } };
        case 'removeSchemaItem': return () => onRemove?.();
        // an added PI Procedure saves its content (description, materials, ...) into its own schema item;
        // ponytail: writes for other items (the procedure's "add step") are dropped, use Add Field instead
        case 'setSchemaItemAt': return (item?: FBSchemaProps) => {
          if (item && item.name === mpiSchema[position]?.name) { onEdit?.(item); }
        };
        // blanktplform is only editable in 'design' mode; deviations author their own template files
        case 'mode': return mpiSchema[position]?.type === 'blanktplform' ? 'design' : Reflect.get(target, prop);
        default: {
          const value = Reflect.get(target, prop);
          return typeof value === 'function' ? value.bind(target) : value;
        }
      }
    },
  }), [workspaceState]);

  const inputProps = mpiSchema[position];
  const control = FBSchemaState.component({
    ...inputProps,
    gutter: false,
    autosave: false,
    // same as the MPI: cloned components are locked except the blank template form; added ones are editable
    disabled: !canEdit || Boolean(
      (!stepData.added && inputProps?.type !== 'blanktplform')
      || inputProps?.disabled
      || inputProps?.outputDisabled,
    ),
    key: `${mpiId}-${stepId}`,
  } as FBInputProps);

  if (!stepData.added || !canEdit) {
    return <FBProvider workspaceState={mpiWorkspace}>{control}</FBProvider>;
  }

  // Added components get the standard field toolbar (drag, markdown, edit, remove), as everywhere else.
  // Not FBFieldWrapper: its hover "Add Field" bar would write into the deviation's own schema.
  return (
    <FBProvider workspaceState={mpiWorkspace}>
      <Box {...{ ref: hoverRef }} className={fieldClasses.root}>
        <Box
          className={fieldClasses.tools}
          hidden={!isHovered}
          onClickCapture={(e: React.MouseEvent) => {
            // only the pencil opens the editor: route its save to this component
            if ((e.target as Element).closest('[data-cy="edit-schema-item"]')) {
              FBDeviationProcedureState.onSave = onEdit;
            }
          }}
        >
          <FBFieldTopBar inputProps={inputProps} toggleItemMarkdown={() => setIsMarkDownVisible((v) => !v)} />
        </Box>
        <Box className={fieldClasses.field}>{control}</Box>
        {isMarkDownVisible && <FBFieldMarkDown inputProps={inputProps} />}
      </Box>
    </FBProvider>
  );
};

export default FBDeviationMpiComponent;
